/* eslint-disable react-hooks/rules-of-hooks */
/**
 * User Store
 * Manages user session state and authentication
 */

import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import apiClient from "../lib/api";
import { useApiStore } from "./apiStore";

// Allowed user levels for login
const ALLOWED_LEVELS = [0];

// User data structure
const initialUserState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoggingIn: false,
};

/**
 * User Store
 * Handles all user-related state and actions
 */
export const useUserStore = create(
  devtools(
    persist(
      (set, get) => ({
        ...initialUserState,

        login: async (credentials) => {
          const state = get();

          // Prevent duplicate login attempts
          if (state.isLoggingIn) {
            return { success: false, error: "Login already in progress" };
          }

          try {
            set({ isAuthenticated: false, isLoggingIn: true });

            const response = await apiClient.post("/auth", credentials);

            if (response.error) {
              const errorMessage = Array.isArray(response.result)
                ? response.result.join(", ")
                : response.result || "Login failed";

              return {
                success: false,
                error: errorMessage,
              };
            }

            const { result } = response;

            // Validate user level before allowing login
            if (
              result?.level !== undefined &&
              !ALLOWED_LEVELS.includes(result.level)
            ) {
              set({ isLoggingIn: false });
              return {
                success: false,
                error:
                  "Access denied. Your user level does not have permission to access this system.",
              };
            }

            if (result?.session?.access_token) {
              apiClient.setAuthToken(result?.session?.access_token);
            }

            set({
              user: result,
              isAuthenticated: true,
              token: result?.session?.access_token,
              isLoggingIn: false,
            });

            return { success: true, user: result, session: result?.session };
          } catch (error) {
            console.error("Login failed:", error);

            let errorMessage = "Login failed";

            if (error.data && error.data.error) {
              // Backend error format in catch block
              errorMessage = Array.isArray(error.data.result)
                ? error.data.result.join(", ")
                : error.data.result || error.message;
            } else if (error.message) {
              errorMessage = error.message;
            }

            set({ isLoggingIn: false });

            return {
              success: false,
              error: errorMessage,
            };
          }
        },

        logout: async () => {
          let apiCallSuccessful = false;

          try {
            await apiClient.post("/auth/logout");
            apiCallSuccessful = true;
          } catch (error) {
            console.log("Logout failed:", error);
          } finally {
            apiClient.setAuthToken(null);
            set({ ...initialUserState });

            // Clear API configuration
            useApiStore.getState().resetToDefault();

            // Clear business state if available (optional dependency)
            if (typeof window !== "undefined" && window.clearBusinessState) {
              window.clearBusinessState();
            }

            if (typeof window !== "undefined") {
              localStorage.removeItem("business-storage");

              const keysToRemove = [];
              for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (
                  key &&
                  (key.includes("auth") ||
                    key.includes("session") ||
                    key.includes("user") ||
                    key.includes("business"))
                ) {
                  keysToRemove.push(key);
                }
              }
              keysToRemove.forEach((key) => localStorage.removeItem(key));
            }
          }

          return {
            success: true,
            apiCallSuccessful,
          };
        },

        refreshUser: async () => {
          try {
            const response = await apiClient.get("/auth/me");

            set({
              user: response.user,
              token: response.user?.session?.access_token,
              isAuthenticated: true,
            });

            return { success: true, user: response.user };
          } catch (error) {
            console.error("User refresh failed:", error);
            await get().logout();
            return {
              success: false,
              error: error.message || "Session expired",
            };
          }
        },

        validateUserFromToken: async () => {
          try {
            const response = await apiClient.get("/users/me");

            if (response.error === false && response.result) {
              const userData = response.result;
              const currentToken = apiClient.getAuthToken();

              set({
                user: userData,
                token: currentToken || userData?.session?.access_token,
                isAuthenticated: true,
              });

              return { success: true, user: userData };
            } else {
              throw new Error("Invalid response format");
            }
          } catch (error) {
            await get().logout();
            return {
              success: false,
              error: error.message || "Token validation failed",
            };
          }
        },

        getPermissions: () => {
          return get().permissions;
        },

        hasPermission: (permission) => {
          const permissions = get().permissions;
          return (
            permissions.includes(permission) || permissions.includes("admin")
          );
        },

        isLoggedIn: () => {
          return get().isAuthenticated;
        },

        // Update user data in store (used by TanStack Query)
        updateUser: (userData) => {
          set({
            user: { ...get().user, ...userData },
            isAuthenticated: true,
          });
        },

        // Check if user has valid session token
        checkAuthStatus: () => {
          const token = apiClient.getAuthToken();
          const state = get();
          return !!(token && state.isAuthenticated);
        },

        // Initialize auth status from stored token
        initializeAuth: async () => {
          const currentState = get();

          let tokenFromUrl = null;
          let projectFromUrl = null;
          const currentProject = useApiStore.getState().project;

          if (typeof window !== "undefined") {
            const searchParams = new URLSearchParams(window.location.search);
            tokenFromUrl =
              searchParams.get("access_token") || searchParams.get("token");
            projectFromUrl = searchParams.get("project");

            if (tokenFromUrl) {
              apiClient.setAuthToken(tokenFromUrl);
            }

            if (projectFromUrl) {
              useApiStore.getState().setProject(projectFromUrl);
            }

            if (tokenFromUrl || projectFromUrl) {
              searchParams.delete("access_token");
              searchParams.delete("token");
              searchParams.delete("project");
              const newSearch = searchParams.toString();
              const newUrl =
                window.location.pathname +
                (newSearch ? "?" + newSearch : "") +
                window.location.hash;
              window.history.replaceState({}, "", newUrl);
            }
          }

          const token = apiClient.getAuthToken();

          if (
            currentState.isAuthenticated &&
            token &&
            (projectFromUrl === currentProject || !projectFromUrl)
          ) {
            return;
          }

          if (token) {
            if (tokenFromUrl) {
              await get().validateUserFromToken();
            } else {
              await get().refreshUser();
            }
          } else {
            // No token, just clear local state without API call
            apiClient.setAuthToken(null);
            set({ ...initialUserState });
            // Clear business state when no token (optional dependency)
            if (typeof window !== "undefined" && window.clearBusinessState) {
              window.clearBusinessState();
            }
          }
        },
      }),
      {
        name: "user-store",
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
        }),
      },
    ),
    {
      name: "user-store",
    },
  ),
);

export const isAuthenticated = () =>
  useUserStore((state) => state.isAuthenticated);
export const userInfo = () => useUserStore((state) => state.user);
export const userToken = () => useUserStore((state) => state.token);
